import api from '../config/api';

/**
 * Trips Service
 * Handles all trip-related API calls
 */

/**
 * Get all trips with filters and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search query
 * @param {string} params.trip_status - Filter by trip status
 * @param {number} params.customer_id - Filter by customer ID
 * @param {number} params.agency_id - Filter by agency ID
 * @param {number} params.vehicle_id - Filter by vehicle ID
 * @param {number} params.assigned_driver_id - Filter by driver ID
 * @param {number} params.route_id - Filter by route ID
 * @param {string} params.start_date - Start date filter (ISO format)
 * @param {string} params.end_date - End date filter (ISO format)
 * @param {number} params.limit - Items per page (default: 10)
 * @param {number} params.offset - Offset for pagination
 * @param {string} params.order_by - Field to order by
 * @param {string} params.sort_order - Sort order (asc or desc)
 * @returns {Promise} API response with trips data
 */
export const getTrips = async (params = {}) => {
  try {
    const {
      search,
      trip_status,
      customer_id,
      agency_id,
      vehicle_id,
      assigned_driver_id,
      route_id,
      start_date,
      end_date,
      limit = 10,
      offset = 0,
      order_by,
      sort_order,
      filter_type
    } = params;

    // Build query parameters
    const queryParams = {
      limit,
      offset,
    };

    if (search) queryParams.search = search;
    if (trip_status) queryParams.trip_status = trip_status;
    if (customer_id) queryParams.customer_id = customer_id;
    if (agency_id) queryParams.agency_id = agency_id;
    if (vehicle_id) queryParams.vehicle_id = vehicle_id;
    if (assigned_driver_id) queryParams.assigned_driver_id = assigned_driver_id;
    if (route_id) queryParams.route_id = route_id;
    if (start_date) queryParams.start_date = start_date;
    if (end_date) queryParams.end_date = end_date;
    if (order_by) queryParams.order_by = order_by;
    if (sort_order) queryParams.sort_order = sort_order;
    if (filter_type) queryParams.filter_type = filter_type;

    const response = await api.get('/api/trips', { params: queryParams });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch trips. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get form data for trips (Agencies, Drivers, Vehicles)
 * @returns {Promise} API response with form data
 */
export const getTripsFormData = async () => {
  try {
    const response = await api.get('/api/trips/form-data');
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch form data. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Export trips with filters
 * @param {Object} params - Query parameters (same as getTrips)
 * @returns {Promise} Blob response for download
 */
export const exportTrips = async (params = {}) => {
  try {
    const queryParams = {};
    
    if (params.search) queryParams.search = params.search;
    if (params.trip_status) queryParams.trip_status = params.trip_status;
    if (params.customer_id) queryParams.customer_id = params.customer_id;
    if (params.agency_id) queryParams.agency_id = params.agency_id;
    if (params.vehicle_id) queryParams.vehicle_id = params.vehicle_id;
    if (params.assigned_driver_id) queryParams.assigned_driver_id = params.assigned_driver_id;
    if (params.route_id) queryParams.route_id = params.route_id;
    if (params.start_date) queryParams.start_date = params.start_date;
    if (params.end_date) queryParams.end_date = params.end_date;

    const response = await api.get('/api/trips/export', {
      params: queryParams,
      responseType: 'blob', // Important for file downloads
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to export trips. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get a single trip by ID
 * @param {number|string} id - Trip ID
 * @param {boolean} include_stops - Include stops in response (default: true)
 * @returns {Promise} API response with trip data
 */
export const getTripById = async (id, include_stops = true) => {
  try {
    const response = await api.get(`/api/trips/${id}`, {
      params: { include_stops }
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch trip. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Download tripsheet for a trip
 * @param {number|string} id - Trip ID
 * @param {string} format - Format type (excel or pdf, default: excel)
 * @returns {Promise} Blob response for download
 */
export const downloadTripsheet = async (id, format = 'excel') => {
  try {
    const response = await api.get(`/api/trips/${id}/tripsheet`, {
      params: { format },
      responseType: 'blob', // Important for file downloads
    });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to download tripsheet. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new trip
 * @param {Object} tripData - Trip data
 * @returns {Promise} API response with created trip data
 */
export const createTrip = async (tripData) => {
  try {
    const response = await api.post('/api/trips', tripData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to create trip. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Edit trip (POST method)
 * @param {number|string} id - Trip ID
 * @param {Object} tripData - Updated trip data
 * @returns {Promise} API response with updated trip data
 */
export const editTrip = async (id, tripData) => {
  try {
    const response = await api.post(`/api/trips/${id}/edit`, tripData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to edit trip. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update trip (PUT method)
 * @param {number|string} id - Trip ID
 * @param {Object} tripData - Updated trip data
 * @returns {Promise} API response with updated trip data
 */
export const updateTrip = async (id, tripData) => {
  try {
    const response = await api.put(`/api/trips/${id}`, tripData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update trip. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Cancel a trip
 * @param {number|string} id - Trip ID
 * @param {Object} cancelData - Optional cancellation data (reason, etc.)
 * @returns {Promise} API response
 */
export const cancelTrip = async (id, cancelData = {}) => {
  try {
    const response = await api.post(`/api/trips/${id}/cancel`, cancelData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to cancel trip. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a trip
 * @param {number|string} id - Trip ID
 * @returns {Promise} API response
 */
export const deleteTrip = async (id) => {
  try {
    const response = await api.delete(`/api/trips/${id}`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to delete trip. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Trip Stops Service
 * Handles all trip stop-related API calls
 */

/**
 * Get stops by trip ID
 * @param {number|string} trip_id - Trip ID
 * @returns {Promise} API response with stops data
 */
export const getStopsByTripId = async (trip_id) => {
  try {
    const response = await api.get(`/api/trip-stops/trip/${trip_id}`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch trip stops. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get all trip stops with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.trip_id - Filter by trip ID
 * @param {number} params.limit - Items per page (default: 10)
 * @param {number} params.offset - Offset for pagination
 * @param {string} params.order_by - Field to order by
 * @param {string} params.sort_order - Sort order (asc or desc)
 * @returns {Promise} API response with stops data
 */
export const getAllTripStops = async (params = {}) => {
  try {
    const {
      trip_id,
      limit = 10,
      offset = 0,
      order_by,
      sort_order
    } = params;

    const queryParams = {
      limit,
      offset,
    };

    if (trip_id) queryParams.trip_id = trip_id;
    if (order_by) queryParams.order_by = order_by;
    if (sort_order) queryParams.sort_order = sort_order;

    const response = await api.get('/api/trip-stops', { params: queryParams });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch trip stops. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get a single trip stop by ID
 * @param {number|string} id - Stop ID
 * @returns {Promise} API response with stop data
 */
export const getTripStopById = async (id) => {
  try {
    const response = await api.get(`/api/trip-stops/${id}`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch trip stop. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new trip stop
 * @param {Object} stopData - Stop data
 * @returns {Promise} API response with created stop data
 */
export const createTripStop = async (stopData) => {
  try {
    const response = await api.post('/api/trip-stops', stopData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to create trip stop. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update a trip stop
 * @param {number|string} id - Stop ID
 * @param {Object} stopData - Updated stop data
 * @returns {Promise} API response with updated stop data
 */
export const updateTripStop = async (id, stopData) => {
  try {
    const response = await api.put(`/api/trip-stops/${id}`, stopData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update trip stop. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a trip stop
 * @param {number|string} id - Stop ID
 * @returns {Promise} API response
 */
export const deleteTripStop = async (id) => {
  try {
    const response = await api.delete(`/api/trip-stops/${id}`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to delete trip stop. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Trip Schedule Stops Service Functions
 */

/**
 * Get stops by schedule ID
 * @param {number|string} schedule_id - Schedule ID
 * @returns {Promise} API response with stops data
 */
export const getStopsByScheduleId = async (schedule_id) => {
  try {
    // Try path parameter first (similar to trip stops: /api/trip-stops/trip/:id)
    try {
      const response = await api.get(`/api/trip-schedule-stops/schedule/${schedule_id}`);
      console.log('getStopsByScheduleId: Path parameter response:', response.data);
      return response.data;
    } catch (pathError) {
      // If path parameter fails, try query parameter
      const response = await api.get(`/api/trip-schedule-stops`, { 
        params: { schedule_id } 
      });
      console.log('getStopsByScheduleId: Query parameter response:', response.data);
      return response.data;
    }
  } catch (error) {
    console.error('getStopsByScheduleId: Error fetching stops:', error);
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch schedule stops. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get a specific trip schedule stop by ID
 * @param {number|string} id - Stop ID
 * @returns {Promise} API response with stop data
 */
export const getTripScheduleStopById = async (id) => {
  try {
    const response = await api.get(`/api/trip-schedule-stops/${id}`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch trip schedule stop. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new trip schedule stop
 * @param {Object} stopData - Stop data
 * @returns {Promise} API response with created stop data
 */
export const createTripScheduleStop = async (stopData) => {
  try {
    const response = await api.post('/api/trip-schedule-stops', stopData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to create trip schedule stop. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update a trip schedule stop
 * @param {number|string} id - Stop ID
 * @param {Object} stopData - Updated stop data
 * @returns {Promise} API response with updated stop data
 */
export const updateTripScheduleStop = async (id, stopData) => {
  try {
    const response = await api.put(`/api/trip-schedule-stops/${id}`, stopData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update trip schedule stop. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a trip schedule stop
 * @param {number|string} id - Stop ID
 * @returns {Promise} API response
 */
export const deleteTripScheduleStop = async (id) => {
  try {
    const response = await api.delete(`/api/trip-schedule-stops/${id}`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to delete trip schedule stop. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Trip Schedules Service Functions
 */

/**
 * Get all trip schedules with filters and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search query
 * @param {string} params.schedule_type - Filter by schedule type (daily, weekly, monthly)
 * @param {string} params.trip_status - Filter by trip status
 * @param {number} params.agency_id - Filter by agency ID
 * @param {number} params.vehicle_id - Filter by vehicle ID
 * @param {number} params.assigned_driver_id - Filter by driver ID
 * @param {string} params.start_date - Start date filter (ISO format)
 * @param {string} params.end_date - End date filter (ISO format)
 * @param {number} params.limit - Items per page (default: 10)
 * @param {number} params.offset - Offset for pagination
 * @returns {Promise} API response with schedules data
 */
export const getTripSchedules = async (params = {}) => {
  try {
    const {
      search,
      filter_type,
      schedule_type,
      trip_status,
      agency_id,
      vehicle_id,
      assigned_driver_id,
      start_date,
      end_date,
      limit = 10,
      offset = 0
    } = params;

    const queryParams = {
      limit,
      offset,
    };

    if (search) queryParams.search = search;
    if (filter_type) queryParams.filter_type = filter_type;
    if (schedule_type) queryParams.schedule_type = schedule_type;
    if (trip_status) queryParams.trip_status = trip_status;
    if (agency_id) queryParams.agency_id = agency_id;
    if (vehicle_id) queryParams.vehicle_id = vehicle_id;
    if (assigned_driver_id) queryParams.assigned_driver_id = assigned_driver_id;
    if (start_date) queryParams.start_date = start_date;
    if (end_date) queryParams.end_date = end_date;

    const response = await api.get('/api/trip-schedules', { params: queryParams });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch trip schedules. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get a single trip schedule by ID
 * @param {number|string} id - Schedule ID
 * @returns {Promise} API response with schedule data
 */
export const getTripScheduleById = async (id) => {
  try {
    const response = await api.get(`/api/trip-schedules/${id}`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to fetch trip schedule. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new trip schedule
 * @param {Object} scheduleData - Schedule data
 * @returns {Promise} API response with created schedule data
 */
export const createTripSchedule = async (scheduleData) => {
  try {
    const response = await api.post('/api/trip-schedules', scheduleData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to create trip schedule. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update a trip schedule
 * @param {number|string} id - Schedule ID
 * @param {Object} scheduleData - Updated schedule data
 * @returns {Promise} API response with updated schedule data
 */
export const updateTripSchedule = async (id, scheduleData) => {
  try {
    const response = await api.put(`/api/trip-schedules/${id}`, scheduleData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to update trip schedule. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a trip schedule
 * @param {number|string} id - Schedule ID
 * @returns {Promise} API response
 */
export const deleteTripSchedule = async (id) => {
  try {
    const response = await api.delete(`/api/trip-schedules/${id}`);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'Failed to delete trip schedule. Please try again.';
    throw new Error(errorMessage);
  }
};

